import {
  Card,
  IconButton,
  Icon,
  Box,
  Switch,
  Tooltip,
  Typography,
  Table,
  TableBody,
  TableRow,
  TableHead,
  TableCell,
  TableContainer,
  Chip,
} from "@mui/material";
import { useState, useEffect } from "react";
import moment from "moment";
import AppService from "../../../../Configurations/Appservice";
import TableLoader from "../../../../Components/AppLoader/TableLoader";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { updateScreenTitle } from "../../../../LayoutContainers/AppLayout/redux/AppSlice";

const columns = [
  { label: "Name", width: "18%" },
  { label: "Phone Number", width: "14%" },
  { label: "Email", width: "22%" },
  { label: "Organisation", width: "16%" },
  { label: "Verified", width: "8%", align: "center" },
  { label: "Created", width: "12%" },
  { label: "", width: "5%", align: "center" },
];

const displayValue = (val) => val || "-";

const headCellSx = {
  fontSize: "0.65rem",
  fontWeight: 700,
  textTransform: "uppercase",
  color: "text.secondary",
  letterSpacing: "0.5px",
  py: 1,
  px: 1.5,
  borderBottom: "2px solid",
  borderColor: "grey.300",
  whiteSpace: "nowrap",
};

const bodyCellSx = {
  fontSize: "0.8rem",
  color: "dark.main",
  py: 0.75,
  px: 1.5,
  borderBottom: "1px solid",
  borderColor: "grey.200",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const SectionTable = ({ title, users, updatingVerification, onVerificationToggle, onEdit }) => {
  return (
    <Card
      sx={{
        borderRadius: 2,
        boxShadow: "0 1px 4px 0 rgba(0,0,0,0.08)",
        overflow: "hidden",
      }}
    >
      <Box
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: "1px solid",
          borderColor: "grey.200",
          bgcolor: "grey.50",
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
          <Typography
            variant="subtitle2"
            sx={{ fontWeight: 600, fontSize: "0.85rem", color: "dark.main" }}
          >
            {title}
          </Typography>
          <Chip
            label={users.length}
            size="small"
            sx={{
              height: 20,
              fontSize: "0.7rem",
              fontWeight: 600,
              bgcolor: "primary.main",
              color: "#fff",
            }}
          />
        </Box>
      </Box>
      <TableContainer sx={{ overflowX: "hidden" }}>
        <Table sx={{ width: "100%", tableLayout: "fixed" }}>
          <TableHead>
            <TableRow>
              {columns.map((col, i) => (
                <TableCell
                  key={i}
                  align={col.align || "left"}
                  sx={{ ...headCellSx, width: col.width }}
                >
                  {col.label}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {users.length === 0 ? (
              <TableRow>
                <TableCell
                  colSpan={columns.length}
                  sx={{ textAlign: "center", py: 3, color: "text.secondary", fontSize: "0.8rem" }}
                >
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              users.map((user) => (
                <TableRow
                  key={user.id}
                  sx={{
                    "&:hover": { bgcolor: "grey.50" },
                    transition: "background-color 0.15s",
                  }}
                >
                  <TableCell sx={bodyCellSx}>{displayValue(user.name)}</TableCell>
                  <TableCell sx={bodyCellSx}>{displayValue(user.phoneNumber)}</TableCell>
                  <TableCell sx={bodyCellSx}>{displayValue(user.emailId)}</TableCell>
                  <TableCell sx={bodyCellSx}>{displayValue(user.organisationName)}</TableCell>
                  <TableCell sx={{ ...bodyCellSx, overflow: "visible" }} align="center">
                    <Tooltip
                      title={user.isVerified ? "Verified - Click to unverify" : "Not verified - Click to verify"}
                      arrow
                    >
                      <Switch
                        checked={user.isVerified || false}
                        onChange={() => onVerificationToggle(user.id, user.isVerified)}
                        disabled={updatingVerification === user.id}
                        size="small"
                        color="success"
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell sx={bodyCellSx}>
                    {user.createDateTime
                      ? moment.utc(user.createDateTime).local().format("YYYY-MM-DD")
                      : "-"}
                  </TableCell>
                  <TableCell sx={{ ...bodyCellSx, overflow: "visible" }} align="center">
                    <Tooltip title="Edit user" arrow>
                      <IconButton size="small" onClick={() => onEdit(user.id)}>
                        <Icon sx={{ fontSize: "1.1rem" }}>edit</Icon>
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  );
};

const AgentUsers = () => {
  const [users, setUsers] = useState([]);
  const [isLoading, loading] = useState(true);
  const [updatingVerification, setUpdatingVerification] = useState(null);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const fetchUsers = async () => {
    const params = { type: "ADMIN_ORG" };
    const response = await AppService.get("v1/user/byType", { params });
    setUsers(response);
    return response;
  };

  const handleVerificationToggle = async (userId, currentValue) => {
    setUpdatingVerification(userId);
    try {
      await AppService.put(`v1/user/${userId}/verification`, null, {
        params: { verified: !currentValue },
      });
      await fetchUsers();
    } catch (error) {
      console.error("Error updating verification status:", error);
    } finally {
      setUpdatingVerification(null);
    }
  };

  useEffect(() => {
    dispatch(updateScreenTitle("Agent Users"));
    return () => dispatch(updateScreenTitle(""));
  }, []);

  useEffect(() => {
    fetchUsers()
      .catch(() => {})
      .finally(() => loading(false));
  }, []);

  const handleEdit = (userId) => {
    navigate("/ADM-AGENT/edit-agent-user", { state: { userId } });
  };

  if (isLoading) {
    return (
      <Box py={1}>
        <Card>
          <TableLoader />
        </Card>
      </Box>
    );
  }

  const adminUsers = users.filter((u) => u.admin);
  const agentUsers = users.filter((u) => !u.admin);

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3, pb: 2 }}>
      <SectionTable
        title="Root Admin Users"
        users={adminUsers}
        updatingVerification={updatingVerification}
        onVerificationToggle={handleVerificationToggle}
        onEdit={handleEdit}
      />
      <SectionTable
        title="Agents (Root Employee)"
        users={agentUsers}
        updatingVerification={updatingVerification}
        onVerificationToggle={handleVerificationToggle}
        onEdit={handleEdit}
      />
    </Box>
  );
};

export default AgentUsers;
